import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useWebSocket } from './hooks/useWebSocket';
import { LobbyView } from './components/LobbyView';
import { WaitingView } from './components/WaitingView';
import { HintingView } from './components/HintingView';
import { GuessView } from './components/GuessView';
import { GameOverView } from './components/GameOverView';
import { CelebrationOverlay } from './components/CelebrationOverlay';
import { NotificationView } from './components/NotificationView';
import { ScoreboardOverlay } from './components/ScoreboardOverlay';
import { SupportPage } from './components/SupportPage';
import { PrivacyPage } from './components/PrivacyPage';
import { LandingPage } from './components/LandingPage';
import { CelebrationData, GameScreen, WSMessage, Hint, PlayerScore } from './types';

function GameApp() {
  const webSocket = useWebSocket();
  
  // Player state
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [, setPlayerName] = useState<string>('');
  const [roomCode, setRoomCode] = useState<string>('');
  const [isMaster, setIsMaster] = useState(false);
  const [playerCount, setPlayerCount] = useState(1);
  const [masterName, setMasterName] = useState<string>('Master');
  const [masterId, setMasterId] = useState<string | null>(null);
  const [playerIdToName, setPlayerIdToName] = useState<Record<string, { name: string; isOnline: boolean }>>({});
  const [playerScores, setPlayerScores] = useState<Record<string, PlayerScore>>({});
  const [, setConnectedPlayers] = useState<string[]>([]);
  const [roomType, setRoomType] = useState<'friends_mode' | 'ai_player_mode'>('friends_mode');
  
  // Game state
  const [currentScreen, setCurrentScreen] = useState<GameScreen>('lobby');
  const [revealedLetters, setRevealedLetters] = useState<string>('');
  const [activeHint, setActiveHint] = useState<Hint | null>(null);
  const [hints, setHints] = useState<Hint[]>([]);
  const [secretWord, setSecretWord] = useState<string>('');
  const [, setChainBreaks] = useState(0);
  const [, setCanStartGame] = useState(false);
  
  // Timers
  const [hintTimeRemaining, setHintTimeRemaining] = useState(0);
  const [hintTotalTime, setHintTotalTime] = useState<number | null>(null);
  const [hintTimerComplete, setHintTimerComplete] = useState(false);
  const [, setHintTimerMessage] = useState('');
  
  const [, setVoteTimeRemaining] = useState(0);
  const [, setVoteTotalTime] = useState<number | null>(null);
  const [, setVoteTimerComplete] = useState(false);
  
  const [guessTimeRemaining, setGuessTimeRemaining] = useState(0);
  const [guessTotalTime, setGuessTotalTime] = useState<number | null>(null);
  const [guessTimerComplete, setGuessTimerComplete] = useState(false);
  const [, setGuessTimerMessage] = useState('');
  const [, setGuessDeadline] = useState<Date | null>(null);
  
  // UI state
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationData, setCelebrationData] = useState<CelebrationData | null>(null);
  const [playerJoinedNotifications, setPlayerJoinedNotifications] = useState<string[]>([]);
  const [playerSubmittedGuessNotifications, setPlayerSubmittedGuessNotifications] = useState<string[]>([]);
  const [playerVoteHintNotifications, setPlayerVoteHintNotifications] = useState<string[]>([]);
  const [lastFlashedHintId, setLastFlashedHintId] = useState<string | null>(null);
  const [showScoreboard, setShowScoreboard] = useState(false);
  const handleWebSocketMessageRef = useRef<(message: WSMessage) => void>(() => {});

  // Handle WebSocket messages
  const handleWebSocketMessage = (message: WSMessage) => {
    // console.log('Received:', message.type, message.data);
    
    const data = message.data || {};
    
    switch (message.type) {
      case 'room_created':
        setRoomCode(data.roomCode);
        setPlayerId(data.playerId);
        setPlayerName(data.playerName);
        setIsMaster(true);
        setMasterName(data.playerName);
        setMasterId(data.playerId);
        setPlayerCount(1);
        setPlayerIdToName({ [data.playerId]: { name: data.playerName, isOnline: true } });
        setPlayerScores({
          [data.playerId]: {
            id: data.playerId,
            playerName: data.playerName,
            hintOnSuccessfulContact: 0,
            successfulContact: 0,
            masterDefence: 0,
            totalPoints: 0
          }
        });
        setRoomType(data.roomType || 'friends_mode');
        setCurrentScreen('waiting');
        setCanStartGame(false);
        break;
        
      case 'room_joined':
        setRoomCode(data.roomCode);
        setPlayerId(data.playerId);
        setPlayerName(data.playerName);
        // console.log('player count on join:', data.playerCount);
        setPlayerCount(data.playerCount || 1);
        setMasterName(data.masterName || 'Master');
        setMasterId(data.currentMasterId);
        setIsMaster(data.isMaster || false);
        setCanStartGame(data.canStartGame || false);
        setRoomType(data.roomType || 'friends_mode');
        setCurrentScreen('waiting');
        
        // Add self
        setPlayerIdToName(prev => ({ ...prev, [data.playerId]: { name: data.playerName, isOnline: true } }));
        setPlayerScores(prev => ({
          ...prev,
          [data.playerId]: {
            id: data.playerId,
            playerName: data.playerName,
            hintOnSuccessfulContact: 0,
            successfulContact: 0,
            masterDefence: 0,
            totalPoints: 0
          }
        }));
        
        // Add existing players
        if (data.existingPlayersIds) {
          const existingNames = data.existingPlayersIds as Record<string, string>;
          Object.entries(existingNames).forEach(([id, name]) => {
            setPlayerIdToName(prev => ({ ...prev, [id]: { name, isOnline: true } }));
            setPlayerScores(prev => ({
              ...prev,
              [id]: {
                id,
                playerName: name,
                hintOnSuccessfulContact: 0,
                successfulContact: 0,
                masterDefence: 0,
                totalPoints: 0
              }
            }));
          });
        }
        break;
        
      case 'player_joined':
        addPlayerJoinedNotification(`${data.playerName} joined!`);
        setPlayerCount(prev => prev + 1);
        // console.log('player count on player joined:', data.playerCount);
        setCanStartGame(data.canStartGame || false);
        setPlayerIdToName(prev => ({ ...prev, [data.playerId]: { name: data.playerName, isOnline: true } }));
        setPlayerScores(prev => ({
          ...prev,
          [data.playerId]: {
            id: data.playerId,
            playerName: data.playerName,
            hintOnSuccessfulContact: 0,
            successfulContact: 0,
            masterDefence: 0,
            totalPoints: 0
          }
        }));
        break;
        
      case 'player_disconnected':
        setPlayerCount(data.playerCount || playerCount - 1);
        setCanStartGame(data.canStartGame || false);
        setPlayerIdToName(prev => {
          const updated = { ...prev };
          if (updated[data.playerId]) {
            updated[data.playerId] = { ...updated[data.playerId], isOnline: false };
          }
          return updated;
        });
        showPlayerLeftNotification(data.playerId);
        break;
        
      case 'game_started':
        setRevealedLetters(data.revealedLetters || '');
        setSecretWord(data.secretWord || '');
        setHints([]);
        setActiveHint(null);
        setChainBreaks(0);
        setIsMaster(data.isMaster || false);
        setCurrentScreen('hinting');
        setStatusMessage(data.isMaster ? `Your word: ${data.secretWord}` : `Submit hints for words starting with '${data.revealedLetters}'`);
        break;
        
      case 'hint_submitted':
        if (data.hint) {
          const hint = data.hint;
          setHints(prev => [...prev, {
            id: hint.id,
            playerId: hint.playerId,
            playerName: playerIdToName[hint.playerId]?.name || hint.playerId,
            text: hint.text,
            votes: hint.votes || [],
            voteCount: hint.voteCount || 0,
            isActive: false,
            guesses: [],
            completed: false,
            successful: false,
            challenged: false
          }]);
        }
        break;
        
      case 'hint_voted':
        showHintVoteNotification(`${playerIdToName[data.voterId]?.name || 'Someone'} contacted a hint`, data.hintId);
        setHints(prev => prev.map(hint => 
          hint.id === data.hintId 
            ? { ...hint, voteCount: data.voteCount || hint.voteCount, votes: [...hint.votes, data.voterId] }
            : hint
        ));
        break;
        
      case 'hint_activated':
        setCurrentScreen('hinting');
        const activatingPlayerName = playerIdToName[data.playerId]?.name || data.playerId;
        setActiveHint({
          id: data.hintId || data.hint?.id,
          playerId: data.playerId,
          playerName: activatingPlayerName,
          text: data.text || data.hint?.text,
          votes: data.connectedPlayerIds || [],
          voteCount: (data.connectedPlayerIds || []).length,
          isActive: true,
          guesses: [],
          completed: false,
          successful: false,
          challenged: false
        });
        break;
        
      case 'hinting_timer':
        setCurrentScreen('hinting');
        setHintTimeRemaining(data.timeRemaining || 0);
        setHintTotalTime(data.totalTime || 0);
        setHintTimerComplete(data.complete || false);
        setHintTimerMessage(`⏰ ${data.timeRemaining || 0} seconds to Choose Hint!`);
        break;
        
      case 'guess_timer':
        setCurrentScreen('guessing');
        setGuessTimeRemaining(data.timeRemaining || 0);
        setGuessTotalTime(data.totalTime || null);
        setGuessTimerComplete(data.complete || false);
        setGuessTimerMessage(`⏰ ${data.timeRemaining || 0} seconds to guess!`);
        if (data.deadline) {
          setGuessDeadline(new Date(data.deadline));
        }
        break;
        
      case 'guess_submitted':
        if (data.playerId !== playerId) {
          const submittedPlayerName = playerIdToName[data.playerId]?.name || 'A player';
          const message = data.playerId === masterId 
            ? `${submittedPlayerName} submitted challenge!!` 
            : `${submittedPlayerName} submitted guess!!`;
          addGuessSubmittedNotification(message);
        }
        if (activeHint && !activeHint.guesses.some(g => g.playerId === data.playerId)) {
          setActiveHint(prev => prev ? {
            ...prev,
            guesses: [...prev.guesses, { playerId: data.playerId, word: '', isCorrect: false }]
          } : null);
        }
        break;
        
      case 'successful_guess':
        setConnectedPlayers(data.connectedPlayers || []);
        showPlayersGuessedSameCelebration(
          data.guess || '',
          data.revealedLetters || '',
          data.hintText || ''
        );
        updatePlayerScores(data.scores);
        break;
        
      case 'broken_contact':
        showPlayersGuessedDifferentCelebration(data.guesses || []);
        updatePlayerScores(data.scores);
        break;
        
      case 'master_cracked_guess':
        showMasterGuessedCorrectCelebration(data.guess || '');
        updatePlayerScores(data.scores);
        setCanStartGame(data.canStartGame || false);
        setPlayerCount(data.playerCount || playerCount);
        break;
        
      case 'chain_broken':
        setChainBreaks(data.chainBreaks || 0);
        setRevealedLetters(data.revealedLetters || revealedLetters);
        setActiveHint(null);
        setHints([]);
        setStatusMessage(`💥 Chain broken! Breaks: ${data.chainBreaks || 0}`);
        break;
        
      case 'letter_revealed':
        setRevealedLetters(data.revealedLetters || revealedLetters);
        setActiveHint(null);
        setHints([]);
        setStatusMessage(`✨ New letter revealed: ${data.revealedLetters || ''}`);
        break;
        
      case 'game_finished':
        showGameOverPlayersWinCelebration(
          data.guess || '',
          data.secretWord || '',
          isMaster
        );
        updatePlayerScores(data.scores);
        break;
        
      case 'game_over':
      case 'game_completed':
        // Show celebration if there's guess and secretWord data
        if (data.guess && data.secretWord) {
          showGameOverPlayersWinCelebration(
            data.guess,
            data.secretWord,
            isMaster
          );
        }
        
        const nextMasterId = data.nextMasterId || data.masterId;
        setMasterId(nextMasterId);
        setCanStartGame(data.canStartGame || false);
        setPlayerCount(data.playerCount || playerCount);
        
        if (nextMasterId === playerId) {
          setIsMaster(true);
          setStatusMessage('👑 You are the Master, choose a secret word to start the game!');
        } else {
          setIsMaster(false);
          setStatusMessage('Waiting for master to start the game...');
        }
        
        updatePlayerScores(data.scores);
        setHintTimeRemaining(60);
        setHintTotalTime(60);
        setHintTimerComplete(false);
        setCurrentScreen('waiting');
        break;
        
      case 'game_over_by_hinting_timeout':
        showMasterWinsByHintingTimeout();
        const timeoutMasterId = data.nextMasterId || data.masterId;
        setMasterId(timeoutMasterId);
        setStatusMessage('Waiting for master to start the game...');
        
        if (timeoutMasterId === playerId) {
          setIsMaster(true);
          setStatusMessage('👑 You are the Master, choose a secret word to start the game!');
        } else {
          setIsMaster(false);
        }
        
        updatePlayerScores(data.scores);
        setCurrentScreen('waiting');
        break;
        
      case 'timer_update':
        const { phase, timeRemaining, totalTime } = data;
        if (phase === 'hinting') {
          setHintTimeRemaining(timeRemaining);
          setHintTotalTime(totalTime);
          setHintTimerComplete(timeRemaining === 0);
        } else if (phase === 'voting') {
          setVoteTimeRemaining(timeRemaining);
          setVoteTotalTime(totalTime);
          setVoteTimerComplete(timeRemaining === 0);
        } else if (phase === 'guessing') {
          setGuessTimeRemaining(timeRemaining);
          setGuessTotalTime(totalTime);
          setGuessTimerComplete(timeRemaining === 0);
        }
        break;
        
      case 'error':
        handleError(data);
        break;
        
      default:
        // console.log('Unhandled message type:', message.type);
    }
  };

  handleWebSocketMessageRef.current = handleWebSocketMessage;

  useEffect(() => {
    webSocket.setOnMessageReceived((message: WSMessage) => {
      handleWebSocketMessageRef.current(message);
    });
    webSocket.setOnDisconnect(() => {
      setCurrentScreen('lobby');
      setPlayerId(null);
      setRoomCode('');
      setPlayerIdToName({});
      setPlayerScores({});
      setPlayerCount(1);
    });
  }, [webSocket]);

  useEffect(() => {
    if (currentScreen === 'hinting') {
      setHintTimeRemaining(60);
      setHintTotalTime(60);
      setHintTimerComplete(false);
    }
  }, [currentScreen]);

  // Warn before reload/back when in active game
  useEffect(() => {
    const isInActiveGame = currentScreen !== 'lobby';
    
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isInActiveGame) {
        e.preventDefault();
        e.returnValue = 'Leaving or reloading will disconnect you from the game. Are you sure?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [currentScreen]);

  // Notification helpers
  const addPlayerJoinedNotification = (notification: string) => {
    setPlayerJoinedNotifications(prev => [...prev, notification]);
    setTimeout(() => {
      setPlayerJoinedNotifications(prev => prev.filter(n => n !== notification));
    }, 2500);
  };

  const showPlayerLeftNotification = (playerId: string) => {
    const playerName = playerIdToName[playerId]?.name || 'A player';
    const message = `${playerName} left the room`;
    setPlayerJoinedNotifications(prev => [...prev, message]);
    setTimeout(() => {
      setPlayerJoinedNotifications(prev => prev.filter(n => n !== message));
    }, 2500);
  };

  const addGuessSubmittedNotification = (message: string) => {
    setPlayerSubmittedGuessNotifications(prev => [...prev, message]);
    setTimeout(() => {
      setPlayerSubmittedGuessNotifications(prev => prev.filter(n => n !== message));
    }, 2500);
  };

  const showHintVoteNotification = (message: string, hintId?: string) => {
    setPlayerVoteHintNotifications(prev => [...prev, message]);
    
    if (hintId) {
      setLastFlashedHintId(hintId);
      setTimeout(() => {
        if (lastFlashedHintId === hintId) {
          setLastFlashedHintId(null);
        }
      }, 2000);
    }
    
    setTimeout(() => {
      setPlayerVoteHintNotifications(prev => prev.filter(n => n !== message));
    }, 2500);
  };

  const updatePlayerScores = (scores: Record<string, any> | undefined) => {
    if (!scores) return;
    
    Object.entries(scores).forEach(([id, scoreData]: [string, any]) => {
      setPlayerScores(prev => ({
        ...prev,
        [id]: {
          id,
          playerName: playerIdToName[id]?.name || scoreData.playerName || '',
          hintOnSuccessfulContact: scoreData.hintOnSuccessfulContact || 0,
          successfulContact: scoreData.successfulContact || 0,
          masterDefence: scoreData.masterDefence || 0,
          totalPoints: (scoreData.hintOnSuccessfulContact || 0) + 
                       (scoreData.successfulContact || 0) + 
                       (scoreData.masterDefence || 0)
        }
      }));
    });
  };

  // Celebration helpers
  const showPlayersGuessedSameCelebration = (word: string, newLetter: string, hintText: string) => {
    setCelebrationData({ type: 'playersGuessedSame', word, newLetter, hintText, isMaster });
    setShowCelebration(true);
    setTimeout(() => {
      setShowCelebration(false);
      setCelebrationData(null);
    }, 2500);
  };

  const showPlayersGuessedDifferentCelebration = (words: string[]) => {
    setCelebrationData({ type: 'playersGuessedDifferent', words, isMaster });
    setShowCelebration(true);
    setTimeout(() => {
      setShowCelebration(false);
      setCelebrationData(null);
    }, 2500);
  };

  const showMasterGuessedCorrectCelebration = (word: string) => {
    setCelebrationData({ type: 'masterGuessedCorrect', word, isMaster });
    setShowCelebration(true);
    setTimeout(() => {
      setShowCelebration(false);
      setCelebrationData(null);
    }, 2500);
  };

  const showMasterWinsByHintingTimeout = () => {
    setCelebrationData({ type: 'hintTimeoutNoChoice', isMaster });
    setShowCelebration(true);
    setTimeout(() => {
      setShowCelebration(false);
      setCelebrationData(null);
    }, 2500);
  };

  const showGameOverPlayersWinCelebration = (word: string, secretWord: string, isMasterPlayer: boolean) => {
    setCelebrationData({ type: 'gameOverPlayersWin', word, secretWord, isMaster: isMasterPlayer });
    setShowCelebration(true);
    setTimeout(() => {
      setShowCelebration(false);
      setCelebrationData(null);
    }, 2500);
  };

  const dismissCelebration = () => {
    setShowCelebration(false);
    setCelebrationData(null);
  };

  const handleError = (data: any) => {
    const errorType = data.type as string;
    setErrorMessage(data.message || 'Unknown error');
    setShowError(true);
    
    if (errorType === 'room_join_error') {
      setErrorMessage(`Failed to join room: ${data.message || 'Unknown error'}`);
      setCurrentScreen('lobby');
    } else if (errorType === 'rate_limit_exceeded') {
      setErrorMessage("You've run out of free rooms creations. Please wait a moment.");
      setCurrentScreen('lobby');
      webSocket.disconnect();
    }
  };

  const handleConnect = (serverURL: string, roomCode: string, playerName: string) => {
    webSocket.connect(serverURL);
    webSocket.send('join_room', { roomCode, playerName });
  };

  const handleCreateRoom = (serverURL: string, playerName: string) => {
    webSocket.connect(serverURL);
    webSocket.send('create_room', { playerName });
  };

  const handlePlayAI = (serverURL: string, playerName: string) => {
    // console.log('Playing against AI with name:', playerName);
    webSocket.connect(serverURL);
    webSocket.send('create_ai_room', { playerName });
  };

  const handleStartGame = (word: string) => {
    webSocket.send('start_game', { secretWord: word });
  };

  const handleSubmitHint = (hint: string) => {
    webSocket.send('submit_hint', { hint });
  };

  const handleVoteHint = (hintId: string) => {
    webSocket.send('vote_hint', { hintId });
  };

  const handleChooseHint = (hintId: string) => {
    webSocket.send('choose_hint', { hintId });
  };

  const handleSubmitGuess = (word: string) => {
    webSocket.send('submit_guess', { word });
  };

  const handleSkipGuess = () => {
    webSocket.send('submit_guess', { word: '' });
  };

  return (
    <div style={{ width: '100vw', height: '100vh', overflow: 'hidden' }}>
      {currentScreen !== 'lobby' && (
        <button
          onClick={() => setShowScoreboard(true)}
          aria-label="Open scoreboard"
          style={{
            position: 'fixed',
            top: '24px',
            left: '20px',
            width: '44px',
            height: '44px',
            borderRadius: '12px',
            border: 'none',
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            flexDirection: 'column',
            cursor: 'pointer',
            zIndex: 1100
          }}
        >
          <span style={{ width: '18px', height: '2px', background: 'white', display: 'block' }} />
          <span style={{ width: '18px', height: '2px', background: 'white', display: 'block' }} />
          <span style={{ width: '18px', height: '2px', background: 'white', display: 'block' }} />
        </button>
      )}

      {currentScreen === 'lobby' && (
        <LobbyView
          onJoinRoom={handleConnect}
          onCreateRoom={handleCreateRoom}
          onPlayAI={handlePlayAI}
          isConnected={webSocket.isConnected}
        />
      )}
      
      {currentScreen === 'waiting' && (
        <WaitingView
          roomCode={roomCode}
          isMaster={isMaster}
          playerCount={playerCount}
          masterName={masterName}
          onStartGame={handleStartGame}
          playerNames={playerIdToName}
          masterId={masterId || undefined}
          roomType={roomType}
          playerId={playerId}
        />
      )}
      
      {currentScreen === 'hinting' && (
        <HintingView
          revealedLetters={revealedLetters}
          hints={hints}
          playerId={playerId}
          isMaster={isMaster}
          secretWord={secretWord}
          playerNames={playerIdToName}
          hintTimeRemaining={hintTimeRemaining}
          hintTotalTime={hintTotalTime}
          hintTimerComplete={hintTimerComplete}
          onSubmitHint={handleSubmitHint}
          onChooseHint={handleChooseHint}
          onVoteHint={handleVoteHint}
        />
      )}
      
      {currentScreen === 'guessing' && (
        <GuessView
          revealedLetters={revealedLetters}
          activeHint={activeHint}
          playerId={playerId}
          isMaster={isMaster}
          guessTimeRemaining={guessTimeRemaining}
          guessTotalTime={guessTotalTime}
          guessTimerComplete={guessTimerComplete}
          onSubmitGuess={handleSubmitGuess}
          onSkipGuess={handleSkipGuess}
        />
      )}
      
      {currentScreen === 'gameOver' && (
        <GameOverView
          statusMessage={statusMessage}
          onStartNewGame={handleStartGame}
        />
      )}
      
      <NotificationView notifications={playerJoinedNotifications} />
      <NotificationView notifications={playerVoteHintNotifications} />
      <NotificationView notifications={playerSubmittedGuessNotifications} />

      <ScoreboardOverlay
        isOpen={showScoreboard}
        roomCode={roomCode}
        playerScores={playerScores}
        playerId={playerId}
        playerOnlineStatus={playerIdToName}
        onClose={() => setShowScoreboard(false)}
      />
      
      {showCelebration && celebrationData && (
        <CelebrationOverlay
          celebration={celebrationData}
          onDismiss={dismissCelebration}
        />
      )}
      
      {showError && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '30px',
            maxWidth: '400px',
            textAlign: 'center'
          }}>
            <h3 style={{ marginBottom: '10px', color: '#DC2626' }}>Error</h3>
            <p style={{ marginBottom: '20px', color: '#374151' }}>{errorMessage}</p>
            <button
              onClick={() => setShowError(false)}
              style={{
                padding: '10px 20px',
                background: '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/game" element={<GameApp />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
